import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  ShieldCheck,
  Clock,
  BadgeCheck,
} from "lucide-react";

import { FaFacebookF, FaInstagram, FaLinkedinIn, FaYoutube } from "react-icons/fa";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  const SERVICE_ID = "service_aysugroups";
  const TEMPLATE_ID = "template_cdpirki";
  const PUBLIC_KEY = "FvuXBj6MRLY8I0DSx";

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.message) {
      setSuccess("Please fill all fields");
      return;
    }

    setLoading(true);

    emailjs
      .send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          message: form.message,
        },
        PUBLIC_KEY
      )
      .then(
        () => {
          setLoading(false);
          setSuccess("Message sent successfully!");
          setForm({ name: "", email: "", message: "" });
        },
        () => {
          setLoading(false);
          setSuccess("Something went wrong. Try again.");
        }
      );
  };

  return (
    <section
      id="contact"
      className="relative pt-12 pb-20 px-6 overflow-hidden 
      bg-gradient-to-br from-[#fffaf5] via-[#f0fdfa] to-[#f8fafc]"
    >
      {/* SOFT BLOBS */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[350px] h-[350px] bg-amber-100 blur-[100px] rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-[350px] h-[350px] bg-teal-100 blur-[100px] rounded-full" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">

        {/* HEADER */}
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
            Contact <span className="text-teal-600">Us</span>
          </h2>
          <p className="text-slate-500 mt-2 text-sm">
            Let’s build something meaningful together
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">

          {/* LEFT */}
          <div className="lg:col-span-5 space-y-4">

            <div className="space-y-3">
              <a href="tel:+919361226606" className="card">
                <Phone size={18} className="text-teal-600" />
                <div>
                  <p className="font-semibold text-sm">Phone</p>
                  <p className="text-slate-500 text-sm">+91 93612 26606</p>
                </div>
              </a>

              <a href="mailto:aysugroups@gmail.com" className="card">
                <Mail size={18} className="text-teal-600" />
                <div>
                  <p className="font-semibold text-sm">Email</p>
                  <p className="text-slate-500 text-sm">aysugroups@gmail.com</p>
                </div>
              </a>

              <a
                href="https://www.google.com/maps?q=9.1754375,77.8826094"
                target="_blank"
                rel="noopener noreferrer"
                className="card"
              >
                <MapPin size={18} className="text-teal-600" />
                <div>
                  <p className="font-semibold text-sm">Office</p>
                  <p className="text-slate-500 text-sm">
                    Kovilpatti, Tamil Nadu
                  </p>
                </div>
              </a>
            </div>

            {/* SOCIAL */}
            <div className="flex gap-3 pt-2">
              <a href="https://www.youtube.com/@AYSUGROUPS" className="social-btn"><FaYoutube /></a>
              <a href="https://www.facebook.com/people/Kaviya-Akshaya-A/61564407896545/#" className="social-btn"><FaFacebookF /></a>
              <a href="https://www.instagram.com/aysu_groups?utm_source=qr&igsh=MWJqZDJmMGtoMXdrNA==" className="social-btn"><FaInstagram /></a>
              <a href="https://www.linkedin.com/in/akshayaaysu/" className="social-btn"><FaLinkedinIn /></a>
            </div>

            {/* BADGES */}
            <div className="grid grid-cols-3 gap-2 pt-2">
              <div className="badge"><ShieldCheck size={16} /><span>Secure</span></div>
              <div className="badge"><Clock size={16} /><span>24/7</span></div>
              <div className="badge"><BadgeCheck size={16} /><span>Verified</span></div>
            </div>

          </div>

          {/* RIGHT */}
          <div className="lg:col-span-7 space-y-4">

            <form onSubmit={handleSubmit} className="form-box">
              <div className="grid md:grid-cols-2 gap-4">
                <input name="name" placeholder="Your name" value={form.name} onChange={handleChange} className="input"/>
                <input name="email" placeholder="Email" value={form.email} onChange={handleChange} className="input"/>
              </div>

              <textarea name="message" rows="4" placeholder="Your message..." value={form.message} onChange={handleChange} className="input"/>

              <button className="submit-btn">
                <Send size={16}/> {loading ? "Sending..." : "Send Message"}
              </button>

              <AnimatePresence>
                {success && (
                  <motion.div initial={{opacity:0}} animate={{opacity:1}} className="text-center text-teal-600 text-sm">
                    {success}
                  </motion.div>
                )}
              </AnimatePresence>
            </form>

            <div className="map-box">
              <iframe
                title="Map"
                src="https://www.google.com/maps?q=Kovilpatti&output=embed"
                className="w-full h-[250px] rounded-lg"
                loading="lazy"
              />
            </div>

          </div>
        </div>
      </div>

      {/* STYLES */}
      <style jsx>{`
        .card {
          display: flex;
          gap: 12px;
          padding: 16px;
          background: rgba(255,255,255,0.7);
          backdrop-filter: blur(10px);
          border-radius: 14px;
          border: 1px solid #e5e7eb;
          transition: 0.25s;
        }

        .card:hover {
          transform: translateY(-3px);
          border-color: #14b8a6;
        }

        .form-box {
          background: rgba(255,255,255,0.8);
          backdrop-filter: blur(12px);
          padding: 28px;
          border-radius: 20px;
          border: 1px solid #e5e7eb;
        }

        .map-box {
          background: white;
          padding: 8px;
          border-radius: 14px;
          border: 1px solid #e5e7eb;
        }

        .input {
          width: 100%;
          padding: 12px;
          border-radius: 10px;
          border: 1px solid #e5e7eb;
          font-size: 14px;
          background: #ffffff;
        }

        .submit-btn {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #14b8a6, #10b981);
          color: white;
          border-radius: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .social-btn {
          width: 42px;
          height: 42px;
          background: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e5e7eb;
        }

        .badge {
          background: white;
          padding: 8px;
          border-radius: 10px;
          text-align: center;
          font-size: 12px;
          border: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
      `}</style>
    </section>
  );
}